import { useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import FavouriteButton from "components/buttons/favouriteButton";
import DetailListVertical from "components/displays/detailListVertical";
import OutlinedChip from "components/displays/outlinedChip";
import OnDesktop from "components/wrappers/onDesktop";
import OnMobile from "components/wrappers/onMobile";
import { IBUY_PRODUCT_CATEGORY_SHAREPOINT_URL } from "constants/generalConstants";
import { MOBILE_MEDIA_QUERY } from "constants/mediaQueries";
import * as React from "react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { getImg } from "services/search/searchAPIs";
import GradientDivider from "styled/styledComponents/GradientDivider";
import { openUserPopper } from "utils/productListUtils";
import {
    openSmartFormModal,
    SMART_FORM_NON_CAT_ITEM_TYPE_DISPATCHER,
} from "utils/smartFormsUtils";

import noImage from "../../assets/noImage.png";
import axiosApiInstance from "../../services/middleware/axios";
import { SMART_FORM_SEARCH_BY_ID } from "../../services/search/urls";
import alertDispatcher from "../../utils/alertsUtils";
import IBuyPaper from "../displays/iBuyPaper";
import PageNotFound from "../pageNotFound/pageNotFound";
import ProductDetailsSkeleton from "../skeletons/productDetailsSkeleton";
import { contentBoxL2 } from "./../../styled/styledSXProperties/styledBoxSX";

const SmartFormDetails = () => {
    // Collect states and state dispatchers
    const activeOU = useSelector((state) => state.auth.activeOU);
    const userInfoLoading = useSelector(
        (state) => state.general.userInfoLoading
    );
    const { id } = useParams();
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [smartForm, setSmartForm] = useState({});
    const [imgUrl, setImgUrl] = useState(noImage);
    const browsingOU = useSelector((state) => state.auth.activeOU);

    // Load image
    useEffect(() => {
        getImg(smartForm.IMAGE, setImgUrl);
    }, [smartForm]);

    useEffect(() => {
        // Search API must only be called once a user's actice OUs
        // have been retrieved and saved in redux
        if (!userInfoLoading) {
            setNotFound(false);
            // On page load, get the smartForm data from the API
            axiosApiInstance
                .get(SMART_FORM_SEARCH_BY_ID(id))
                .then((response) => {
                    const data = response.data;
                    if (data.length <= 0) {
                        const action = {
                            text: "Change Operating Unit",
                            action: (e) => openUserPopper(e, dispatch),
                        };
                        alertDispatcher(
                            "smartFormDetails",
                            "404",
                            dispatch,
                            "",
                            action
                        );
                        setNotFound(true);
                        setIsLoading(false);
                    } else {
                        let result = data;
                        // Extract associated information templates and inject into
                        // results payload
                        setSmartForm(result);
                        setIsLoading(false);
                    }
                })
                .catch((e) => {
                    alertDispatcher(
                        "smartFormDetails",
                        e?.response?.status,
                        dispatch
                    );
                    setNotFound(true);
                    setIsLoading(false);
                });
        }
    }, [activeOU]);

    // Go to category SharePoint page
    const goToCategoryTree = () => {
        window.open(IBUY_PRODUCT_CATEGORY_SHAREPOINT_URL, "_blank").focus();
    };

    const isDesktop = useMediaQuery(MOBILE_MEDIA_QUERY);

    const generalInfoDetails = [
        {
            header: "Product Type",
            content: "iBuy Smart Form",
        },
        {
            header: "Descriptive keywords",
            content: smartForm?.KEYWORDS,
        },
        {
            header: "Item Type",
            content:
                SMART_FORM_NON_CAT_ITEM_TYPE_DISPATCHER[smartForm?.ITEM_TYPE],
        },
        {
            header: "Category Code",
            content: smartForm?.PURCHASING_CATEGORY,
        },
        {
            header: "Currency",
            content: smartForm?.CURRENCY || "Not specified on Smart Form",
        },
    ];

    const supplierInfoDetails = [
        {
            header: "Associated Contract",
            content:
                smartForm?.CONTRACT ||
                "No contract purchase agreement associated",
        },
        {
            header: "Can contract be edited",
            content: smartForm?.CONTRACT_EDITABLE_FLAG,
        },
        {
            header: "Supplier Name",
            content: smartForm?.SUPPLIER_NAME,
        },
        {
            header: "Supplier Site",
            content: smartForm?.SUPPLIER_SITE_CODE,
        },
    ];

    return (
        <Box sx={{ contentBoxL2, marginTop: 2 }}>
            {notFound ? (
                <PageNotFound />
            ) : !isLoading ? (
                <IBuyPaper>
                    <Grid container spacing={5}>
                        <Grid
                            container
                            item
                            xs={12}
                            md={5}
                            lg={3}
                            direction="column"
                            justifyContent="center"
                            alignContent="center"
                        >
                            <Box
                                component="img"
                                aria-label={smartForm?.TEMPLATE_NAME}
                                alt={smartForm?.TEMPLATE_NAME}
                                src={imgUrl}
                                onError={({ currentTarget }) => {
                                    currentTarget.onerror = null; // prevents looping
                                    currentTarget.src = noImage;
                                }}
                                sx={{
                                    borderRadius: "10px",
                                }}
                                width={isDesktop ? "100%" : "60%"}
                            />
                        </Grid>
                        <Grid container item xs={12} md={9} lg={8}>
                            <Grid container spacing={1}>
                                <Grid
                                    item
                                    container
                                    xs={12}
                                    spacing={isDesktop ? 2 : 0}
                                >
                                    <Grid item xs={10} justifyContent="center">
                                        <Typography
                                            variant={isDesktop ? "h4" : "h6"}
                                            fontWeight="bold"
                                            color="text.header"
                                            marginBottom={2}
                                            textAlign="left"
                                        >
                                            {`Smart Form Summary: ${smartForm?.TEMPLATE_NAME}`}
                                        </Typography>
                                        <OnDesktop>
                                            <Grid item xs={12}>
                                                <GradientDivider />
                                            </Grid>
                                        </OnDesktop>
                                    </Grid>
                                    <OnMobile>
                                        <Grid item xs={2}>
                                            <FavouriteButton
                                                smartForm={smartForm}
                                                white
                                            />
                                        </Grid>
                                    </OnMobile>
                                    <OnMobile>
                                        <Grid item xs={12} paddingTop={1}>
                                            <GradientDivider />
                                        </Grid>
                                    </OnMobile>
                                </Grid>
                                <Grid container item xs={12}>
                                    <Grid
                                        container
                                        item
                                        direction="column"
                                        xs={12}
                                        lg={7}
                                        mb={2}
                                        pt={0}
                                        alignContent={"left"}
                                    >
                                        <Typography
                                            variant="h5"
                                            color="text.subheader"
                                            marginBottom="12px"
                                        >
                                            General Information
                                        </Typography>
                                        <DetailListVertical
                                            condensed={true}
                                            details={generalInfoDetails}
                                        />
                                    </Grid>
                                    <Grid
                                        container
                                        item
                                        direction="column"
                                        alignContent={
                                            isDesktop ? "center" : "left"
                                        }
                                        xs={12}
                                        lg={5}
                                    >
                                        <Typography
                                            variant="h5"
                                            color="text.subheader"
                                            marginBottom="12px"
                                        >
                                            Supplier Information
                                        </Typography>
                                        <DetailListVertical
                                            condensed={true}
                                            details={supplierInfoDetails}
                                        />
                                    </Grid>
                                    {isDesktop && <Grid item lg={6} />}
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    pb={2}
                                    lg={6}
                                    justifyItems="center"
                                    align={isDesktop ? "left" : "center"}
                                >
                                    <OutlinedChip value="Smart Form" />
                                    {smartForm?.CONTRACT && (
                                        <OutlinedChip value="CPA" marginLeft />
                                    )}
                                    {smartForm?.PURCHASING_CATEGORY && (
                                        <OutlinedChip
                                            value={`Category: ${smartForm?.PURCHASING_CATEGORY}`}
                                            marginLeft
                                            onClick={goToCategoryTree}
                                            clickable
                                        />
                                    )}
                                </Grid>
                                <Grid
                                    item
                                    container
                                    xs={12}
                                    mb={isDesktop ? 0 : 1}
                                >
                                    <Grid
                                        item
                                        xs={isDesktop ? 10 : 12}
                                        align={isDesktop ? "left" : "center"}
                                    >
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={() =>
                                                openSmartFormModal(
                                                    smartForm,
                                                    dispatch,
                                                    activeOU
                                                )
                                            }
                                        >
                                            Launch Smart Form
                                        </Button>
                                    </Grid>
                                    <Grid
                                        item
                                        xs={2}
                                        align="center"
                                        justify="center"
                                    >
                                        <OnDesktop>
                                            <FavouriteButton
                                                smartForm={smartForm}
                                                product={null}
                                                activeOU={browsingOU}
                                                white
                                            />
                                        </OnDesktop>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </IBuyPaper>
            ) : (
                <ProductDetailsSkeleton />
            )}
        </Box>
    );
};

export default SmartFormDetails;
